import { z } from 'zod';
import { CUSTOMER_CLASSES, customerClassSql, lastsDaysSql, REPLENISH_WINDOW } from './commerce.ts';

/**
 * Segment rules: who a campaign is for. A segment is a list of conditions that must all hold ("all"). Rules are
 * data (stored as JSON) and compiled to parameterised SQL here; no value from a rule is ever put into SQL text.
 */
const ids = z.array(z.uuid()).min(1).max(200);
const words = z.array(z.string().trim().min(1).max(120)).min(1).max(50);
const days = z.number().int().min(1).max(3650);
const amount = z.number().min(0).max(100_000_000);

export const SegmentRule = z.discriminatedUnion('type', [
  z.object({ type: z.literal('class'), classes: z.array(z.enum(CUSTOMER_CLASSES)).min(1) }),
  z.object({
    type: z.literal('spent'),
    min: amount.optional(),
    max: amount.optional(),
    days: days.optional(),
  }),
  z.object({
    type: z.literal('orders'),
    min: z.number().int().min(0).optional(),
    max: z.number().int().min(0).optional(),
    days: days.optional(),
  }),
  z.object({ type: z.literal('lastOrder'), withinDays: days.optional(), olderThanDays: days.optional() }),
  z.object({
    type: z.literal('bought'),
    brands: words.optional(),
    families: words.optional(),
    categories: words.optional(),
    genders: z
      .array(z.enum(['women', 'men', 'unisex']))
      .min(1)
      .optional(),
    productIds: ids.optional(),
    days: days.optional(),
  }),
  z.object({ type: z.literal('store'), storeIds: ids }),
  z.object({ type: z.literal('channel'), channels: words }),
  z.object({ type: z.literal('birthday'), withinDays: z.number().int().min(0).max(60) }),
  z.object({ type: z.literal('waitlist'), productIds: ids.optional() }),
  z.object({ type: z.literal('replenishDue') }),
  z.object({ type: z.literal('tag'), tags: words }),
  z.object({ type: z.literal('city'), cities: words }),
  /** Asked about something (words in their messages) within N days, and (by default) did not buy since. */
  z.object({
    type: z.literal('asked'),
    words: words,
    days: days.default(30),
    notBought: z.boolean().default(true),
  }),
  z.object({
    type: z.literal('stage'),
    stages: z.array(z.enum(['lead', 'contact', 'customer', 'churned'])).min(1),
  }),
]);
export type SegmentRule = z.infer<typeof SegmentRule>;

export const SegmentRules = z.object({ all: z.array(SegmentRule).max(20) });
export type SegmentRules = z.infer<typeof SegmentRules>;

/** Orders that count as sales (cancelled and returned orders do not). */
export const COUNTED_ORDER = `o.status NOT IN ('cancelled', 'returned')`;

/**
 * Per-person purchase statistics, joined as `cs` by segment queries and customer views. Columns: person_id, orders,
 * orders_year, days_since_last, spent_minor, last_order_at, class.
 */
export const CUSTOMER_STATS_SQL = `
  SELECT s.*, ${customerClassSql('s.orders', 's.orders_year', 's.days_since_last')} AS class
  FROM (
    SELECT o.person_id,
           count(*)::int AS orders,
           (count(*) FILTER (WHERE o.placed_at > now() - interval '365 days'))::int AS orders_year,
           (current_date - max(o.placed_at)::date)::int AS days_since_last,
           sum(o.total_minor)::bigint AS spent_minor,
           max(o.placed_at) AS last_order_at
    FROM orders o WHERE o.person_id IS NOT NULL AND ${COUNTED_ORDER}
    GROUP BY o.person_id
  ) s`;

/**
 * Compiles rules into a WHERE fragment over `p` (persons) and `cs` (CUSTOMER_STATS_SQL, left-joined). Values are
 * appended to `params`; placeholders continue from its current length. `minorUnits` converts amounts (e.g. 100 for
 * QAR, 1000 for KWD).
 */
export function compileSegment(rules: SegmentRules, params: unknown[], minorUnits = 100): string {
  const p = (v: unknown) => {
    params.push(v);
    return `$${params.length}`;
  };
  const since = (d: number | undefined, col = 'o.placed_at') =>
    d ? ` AND ${col} > now() - make_interval(days => ${p(d)})` : '';
  const parts = rules.all.map((r): string => {
    switch (r.type) {
      case 'class':
        return `coalesce(cs.class, 'prospect') = ANY(${p(r.classes)}::text[])`;
      case 'spent': {
        const sum = `(SELECT coalesce(sum(o.total_minor), 0) FROM orders o WHERE o.person_id = p.id AND ${COUNTED_ORDER}${since(r.days)})`;
        const c: string[] = [];
        if (r.min !== undefined) c.push(`${sum} >= ${p(Math.round(r.min * minorUnits))}`);
        if (r.max !== undefined) c.push(`${sum} <= ${p(Math.round(r.max * minorUnits))}`);
        return c.length ? c.join(' AND ') : 'true';
      }
      case 'orders': {
        const n = `(SELECT count(*) FROM orders o WHERE o.person_id = p.id AND ${COUNTED_ORDER}${since(r.days)})`;
        const c: string[] = [];
        if (r.min !== undefined) c.push(`${n} >= ${p(r.min)}`);
        if (r.max !== undefined) c.push(`${n} <= ${p(r.max)}`);
        return c.length ? c.join(' AND ') : 'true';
      }
      case 'lastOrder': {
        const c: string[] = [];
        if (r.withinDays !== undefined) c.push(`cs.days_since_last <= ${p(r.withinDays)}`);
        if (r.olderThanDays !== undefined) c.push(`cs.days_since_last > ${p(r.olderThanDays)}`);
        return c.length ? c.join(' AND ') : 'cs.person_id IS NOT NULL';
      }
      case 'bought': {
        const c = [`o.person_id = p.id`, COUNTED_ORDER];
        if (r.brands) c.push(`lower(pr.brand) = ANY(${p(r.brands.map((b) => b.toLowerCase()))}::text[])`);
        if (r.families)
          c.push(`lower(pr.scent_family) = ANY(${p(r.families.map((b) => b.toLowerCase()))}::text[])`);
        if (r.categories) c.push(`pr.category = ANY(${p(r.categories)}::text[])`);
        if (r.genders) c.push(`pr.gender = ANY(${p(r.genders)}::text[])`);
        if (r.productIds) c.push(`pr.id = ANY(${p(r.productIds)}::uuid[])`);
        return `EXISTS (SELECT 1 FROM orders o JOIN order_items oi ON oi.order_id = o.id
          JOIN products pr ON pr.id = oi.product_id WHERE ${c.join(' AND ')}${since(r.days)})`;
      }
      case 'store':
        return `(p.preferred_store_id = ANY(${p(r.storeIds)}::uuid[]) OR EXISTS (SELECT 1 FROM orders o
          WHERE o.person_id = p.id AND o.store_id = ANY($${params.length}::uuid[]) AND ${COUNTED_ORDER}))`;
      case 'channel':
        return `EXISTS (SELECT 1 FROM conversations c WHERE c.person_id = p.id AND c.channel = ANY(${p(r.channels)}::text[]))`;
      case 'birthday':
        return `p.birthday IS NOT NULL AND EXISTS (SELECT 1 FROM generate_series(0, ${p(r.withinDays)}::int) g
          WHERE to_char(current_date + g, 'MMDD') = to_char(p.birthday, 'MMDD'))`;
      case 'waitlist':
        return `EXISTS (SELECT 1 FROM product_waitlist w WHERE w.person_id = p.id AND w.notified_at IS NULL${
          r.productIds ? ` AND w.product_id = ANY(${p(r.productIds)}::uuid[])` : ''
        })`;
      case 'replenishDue':
        return `EXISTS (${replenishDueSql('p.id')})`;
      case 'tag':
        return `p.tags && ${p(r.tags)}::text[]`;
      case 'city':
        return `lower(p.city) = ANY(${p(r.cities.map((c) => c.toLowerCase()))}::text[])`;
      case 'stage':
        return `p.lifecycle_stage = ANY(${p(r.stages)}::text[])`;
      case 'asked': {
        const like = p(r.words.map((w) => `%${w.toLowerCase().replace(/[\\%_]/g, (c) => `\\${c}`)}%`));
        return `EXISTS (SELECT 1 FROM messages m WHERE m.tenant_id = p.tenant_id AND m.person_id = p.id AND m.direction = 'inbound'
          AND m.created_at > now() - make_interval(days => ${p(r.days)}) AND lower(m.body_text) LIKE ANY(${like}::text[])
          ${r.notBought ? `AND NOT EXISTS (SELECT 1 FROM orders o WHERE o.person_id = p.id AND o.placed_at > m.created_at AND ${COUNTED_ORDER})` : ''})`;
      }
    }
  });
  return parts.length ? parts.map((x) => `(${x})`).join(' AND ') : 'true';
}

/**
 * Purchases whose bottle should be running out about now (and that the person has not bought again since), for
 * one person (`personRef`) or, with `personRef` = 'o.person_id', for everyone. Columns: person_id, product_id,
 * placed_at, due_on.
 */
export function replenishDueSql(personRef: string): string {
  const lasts = lastsDaysSql('pr.category', 'pr.size_ml');
  return `SELECT o.person_id, pr.id AS product_id, o.placed_at, (o.placed_at::date + ${lasts}) AS due_on
    FROM orders o JOIN order_items oi ON oi.order_id = o.id JOIN products pr ON pr.id = oi.product_id
    WHERE o.person_id = ${personRef} AND ${COUNTED_ORDER} AND ${lasts} IS NOT NULL
      AND current_date BETWEEN o.placed_at::date + ${lasts} - ${REPLENISH_WINDOW.beforeDays}
                           AND o.placed_at::date + ${lasts} + ${REPLENISH_WINDOW.afterDays}
      AND NOT EXISTS (SELECT 1 FROM orders o2 JOIN order_items oi2 ON oi2.order_id = o2.id
                      WHERE o2.person_id = o.person_id AND oi2.product_id = pr.id AND o2.placed_at > o.placed_at
                        AND o2.status NOT IN ('cancelled', 'returned'))`;
}
